"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import type { LayoutVariant } from "./layout-variant-types";

export type { LayoutVariant } from "./layout-variant-types";

const VARIANTS: { key: LayoutVariant; label: string }[] = [
  { key: "default", label: "Current" },
  { key: "f1", label: "F1: Flagship" },
  { key: "f2", label: "F2: Compact" },
  { key: "f3", label: "F3: Editorial" },
];

export function LayoutSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentLayout = (searchParams.get("layout") ??
    "default") as LayoutVariant;

  const handleSwitch = useCallback(
    ({ variant }: { variant: LayoutVariant }) => {
      const params = new URLSearchParams(searchParams.toString());
      if (variant === "default") {
        params.delete("layout");
      } else {
        params.set("layout", variant);
      }
      const qs = params.toString();
      router.push(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [router, pathname, searchParams]
  );

  return (
    <div
      className="sticky top-0 z-50 border-[var(--explore-border)] border-b bg-[var(--explore-bg)]/95 backdrop-blur-sm"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div className="mx-auto flex max-w-[1320px] items-center gap-1 overflow-x-auto px-4 py-2 sm:gap-2 sm:px-6">
        <span className="mr-2 shrink-0 font-medium text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.08em]">
          Layout
        </span>
        {VARIANTS.map((v) => (
          <button
            className={`shrink-0 rounded-full px-3 py-1.5 font-medium text-[0.75rem] transition-all ${
              currentLayout === v.key
                ? "bg-[var(--explore-text-primary)] text-[var(--explore-bg)]"
                : "bg-[var(--explore-border-light)] text-[var(--explore-text-secondary)] hover:bg-[var(--explore-border)] hover:text-[var(--explore-text-primary)]"
            }`}
            key={v.key}
            onClick={() => handleSwitch({ variant: v.key })}
            type="button"
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}
